import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";
import { END_POINT } from "../../../constants/defaultValues";
import { AddCommaNumber } from "../../../helpers/Utils";
import IntlMessages from "../../../helpers/IntlMessages";
import { ContentMini } from "../../../components/preloader";

const emptySummary = {
  identified_customers: 0,
  anonymous_orders: 0,
  total_quantity: 0,
  total_revenue: 0,
};

const Stat = ({ value, label }) => (
  <div className="border rounded p-3 text-center h-100">
    <h4 className="mb-1">{AddCommaNumber(value || 0)}</h4>
    <small className="text-muted">{label}</small>
  </div>
);

const ProductCustomerDrawer = ({ product, setting, companyUuid, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({
    summary: emptySummary,
    data: [],
    pagination: { page: 1, total_pages: 1, total: 0 },
  });

  useEffect(() => {
    if (!product || !product.id) return undefined;
    let active = true;
    const token = localStorage.getItem("access_token");
    setLoading(true);
    setError("");
    axios
      .get(`${END_POINT}/analytics/product-customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          product_id: product.id,
          all: setting.all,
          date_start: moment(setting.date_start).format("YYYY-MM-DD"),
          date_end: moment(setting.date_end).format("YYYY-MM-DD"),
          company_uuid: companyUuid,
          search: appliedSearch,
          page,
          limit: 20,
        },
      })
      .then((response) => {
        if (active) setResult(response.data.data);
      })
      .catch(() => {
        if (active) setError("Không thể tải danh sách khách hàng của sản phẩm.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [product, setting.all, setting.date_start, setting.date_end, companyUuid, appliedSearch, page]);

  const summary = result.summary || emptySummary;
  const pagination = result.pagination || { page: 1, total_pages: 1, total: 0 };

  return (
    <Modal isOpen={Boolean(product)} toggle={onClose} size="lg" className="analytics-product-customer-modal">
      <ModalHeader toggle={onClose}>
        <IntlMessages id="analytics.product_customers.title" />: {product ? product.label || product.name : ""}
      </ModalHeader>
      <ModalBody>
        <div className="row mb-4">
          <div className="col-6 col-lg-3 mb-2">
            <Stat value={summary.identified_customers} label={<IntlMessages id="analytics.product_customers.identified" />} />
          </div>
          <div className="col-6 col-lg-3 mb-2">
            <Stat value={summary.anonymous_orders} label={<IntlMessages id="analytics.product_customers.anonymous" />} />
          </div>
          <div className="col-6 col-lg-3 mb-2">
            <Stat value={summary.total_quantity} label={<IntlMessages id="analytics.product_customers.quantity" />} />
          </div>
          <div className="col-6 col-lg-3 mb-2">
            <Stat value={summary.total_revenue} label={<IntlMessages id="analytics.product_customers.revenue" />} />
          </div>
        </div>

        <form
          className="d-flex mb-3"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setAppliedSearch(search.trim());
          }}
        >
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Tìm theo tên, số điện thoại, email hoặc địa chỉ"
          />
          <Button color="primary" className="ml-2" type="submit">
            <IntlMessages id="analytics.product_customers.search" />
          </Button>
        </form>

        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-5"><ContentMini /></div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th><IntlMessages id="analytics.product_customers.customer" /></th>
                  <th className="text-center"><IntlMessages id="analytics.product_customers.orders" /></th>
                  <th className="text-center"><IntlMessages id="analytics.product_customers.quantity" /></th>
                  <th className="text-right"><IntlMessages id="analytics.product_customers.spent" /></th>
                  <th><IntlMessages id="analytics.product_customers.last_purchase" /></th>
                </tr>
              </thead>
              <tbody>
                {(result.data || []).map((buyer) => (
                  <tr key={buyer.buyer_key || buyer.customer_id}>
                    <td>
                      <strong>{buyer.name || "Khách hàng"}</strong>
                      <div className="text-muted small">{buyer.phone || buyer.email || "-"}</div>
                      {buyer.address ? <div className="text-muted small">{buyer.address}</div> : null}
                    </td>
                    <td className="text-center">{buyer.order_count}</td>
                    <td className="text-center">{AddCommaNumber(buyer.quantity)}</td>
                    <td className="text-right">{AddCommaNumber(buyer.total_spent)}</td>
                    <td>{buyer.last_purchase_at || "-"}</td>
                  </tr>
                ))}
                {!result.data || result.data.length === 0 ? (
                  <tr><td colSpan="5" className="text-center text-muted py-4"><IntlMessages id="analytics.product_customers.empty" /></td></tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <span className="text-muted small">{pagination.total || 0} <IntlMessages id="analytics.product_customers.customers" /></span>
        <div>
          <Button color="light" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>‹</Button>
          <span className="mx-3">{page}/{pagination.total_pages || 1}</span>
          <Button color="light" disabled={page >= (pagination.total_pages || 1) || loading} onClick={() => setPage((value) => value + 1)}>›</Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ProductCustomerDrawer;
